/**
 * Journey Loading Component - Premium AI Generation Experience
 *
 * Shows sophisticated staged progress during AI journey generation.
 * This is the USP screen where users see the value of our NRI-specific analysis.
 * Designed for 30-60 second LLM operations with engaging animations.
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, Loader2, Sparkles, Brain, TrendingUp } from 'lucide-react';
import type { ProgressStage } from '@/hooks/useJourney';

interface JourneyLoadingProps {
  stage: ProgressStage;
  message?: string;
}

// 12 NRI-specific analysis stages with TurboTax-style language
// Designed for ~18-20 second total experience
const STAGES = [
  {
    key: 'profile_scan',
    label: 'Scanning your financial profile...',
    description: 'Reviewing income, assets & family details',
    icon: '🔍',
    durationMs: 1200,
  },
  {
    key: 'rnor_check',
    label: 'Checking RNOR tax benefit eligibility...',
    description: 'This could save you lakhs in taxes',
    icon: '🎯',
    durationMs: 1800,
  },
  {
    key: 'move_date',
    label: 'Calculating your optimal move date...',
    description: 'Finding the best time to maximize benefits',
    icon: '📅',
    durationMs: 1600,
  },
  {
    key: 'treaty_analysis',
    label: 'Analyzing US-India tax treaty benefits...',
    description: 'Checking DTAA provisions that apply to you',
    icon: '🏛️',
    durationMs: 2200,
  },
  {
    key: 'capital_gains',
    label: 'Evaluating capital gains strategies...',
    description: 'Timing matters - wrong order can cost 20%+ in taxes',
    icon: '📈',
    durationMs: 2000,
  },
  {
    key: 'retirement_accounts',
    label: 'Reviewing 401(k) & IRA optimization...',
    description: 'Checking Roth conversion & withdrawal strategies',
    icon: '🏦',
    durationMs: 1800,
  },
  {
    key: 'liquidation_sequence',
    label: 'Building asset liquidation sequence...',
    description: 'Creating year-by-year sell order for maximum savings',
    icon: '💼',
    durationMs: 2400,
  },
  {
    key: 'tax_optimization',
    label: 'Maximizing your tax savings...',
    description: 'Finding deductions & exemptions you qualify for',
    icon: '💰',
    durationMs: 2000,
  },
  {
    key: 'repatriation',
    label: 'Planning fund repatriation timeline...',
    description: 'Optimizing when & how to transfer funds to India',
    icon: '🔄',
    durationMs: 1600,
  },
  {
    key: 'compliance_check',
    label: 'Running compliance verification...',
    description: 'Ensuring FBAR, FATCA & RBI regulations are addressed',
    icon: '✓',
    durationMs: 1400,
  },
  {
    key: 'accuracy_review',
    label: 'Performing accuracy review...',
    description: 'Double-checking calculations & recommendations',
    icon: '🔬',
    durationMs: 1200,
  },
  {
    key: 'finalizing',
    label: 'Finalizing your personalized roadmap...',
    description: 'Generating your step-by-step action plan',
    icon: '✨',
    durationMs: 800,
  },
] as const;

// Rotating NRI facts to show during loading (4-5 facts shown in 18 seconds)
const NRI_FACTS = [
  {
    title: 'RNOR Tax Benefit',
    fact: 'RNOR status can save you up to 30% in taxes on foreign income for 2-3 years after returning.',
    icon: '💡'
  },
  {
    title: 'Capital Gains Timing',
    fact: 'Selling US stocks before vs after becoming Indian resident can have 20%+ tax difference.',
    icon: '📊'
  },
  {
    title: 'DTAA Protection',
    fact: 'India-US tax treaty prevents double taxation - but you need to claim benefits correctly.',
    icon: '🛡️'
  },
  {
    title: '401(k) Strategy',
    fact: 'Roth conversions during RNOR can be tax-advantaged. Timing is everything.',
    icon: '🏦'
  },
  {
    title: 'Asset Sequence Matters',
    fact: 'The ORDER you sell assets can save lakhs. RSUs before brokerage? It depends on your situation.',
    icon: '📈'
  },
  {
    title: 'Compliance is Critical',
    fact: 'FBAR, FATCA, Form 67 - missing these can result in penalties up to $10,000 per violation.',
    icon: '⚠️'
  },
];

// Preview cards showing what user will get
const VALUE_PREVIEWS = [
  { title: 'Tax Timeline', subtitle: 'Year-by-year optimization', icon: TrendingUp },
  { title: 'Action Plan', subtitle: 'Prioritized checklist', icon: CheckCircle },
  { title: 'AI Insights', subtitle: 'Personalized recommendations', icon: Brain },
];

export function JourneyLoading({ stage }: JourneyLoadingProps) {
  const [currentFactIndex, setCurrentFactIndex] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);

  // Visual index advances automatically through all stages
  // Each stage has its own duration for realistic pacing
  const [visualIndex, setVisualIndex] = useState(0);

  // Auto-advance through stages with individual timing
  // Stops when backend stage is complete or error
  useEffect(() => {
    // Stop animation if backend is done
    if (stage === 'complete' || stage === 'error') return;
    if (visualIndex >= STAGES.length - 1) return; // Stop at last stage

    const currentStageDuration = STAGES[visualIndex]?.durationMs ?? 1500;

    const timer = setTimeout(() => {
      setVisualIndex(prev => Math.min(prev + 1, STAGES.length - 1));
    }, currentStageDuration);

    return () => clearTimeout(timer);
  }, [visualIndex, stage]);

  // Rotate facts every 4 seconds (show more facts during 18-sec experience)
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentFactIndex((prev) => (prev + 1) % NRI_FACTS.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // Track elapsed time
  useEffect(() => {
    const interval = setInterval(() => {
      setElapsedTime((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const currentFact = NRI_FACTS[currentFactIndex];
  const progress = ((visualIndex + 1) / STAGES.length) * 100;

  return (
    <div className="flex flex-col h-full bg-gradient-to-br from-slate-50 via-blue-50/30 to-emerald-50/20 p-6 overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-10 right-10 w-64 h-64 bg-emerald-200/20 rounded-full blur-3xl"
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
            x: [0, 20, 0],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute bottom-10 left-10 w-72 h-72 bg-blue-200/20 rounded-full blur-3xl"
          animate={{ 
            scale: [1.2, 1, 1.2],
            opacity: [0.4, 0.2, 0.4],
            y: [0, -20, 0],
          }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      <div className="relative z-10 flex flex-col h-full max-w-2xl mx-auto w-full">
        {/* Header */}
        <div className="text-center mb-6">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full shadow-sm border border-emerald-100 mb-4"
          >
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
            >
              <Sparkles className="w-4 h-4 text-emerald-500" />
            </motion.div>
            <span className="text-sm font-medium text-emerald-700">Building Your Personalized Strategy</span>
          </motion.div>

          <h2 className="text-2xl font-bold text-slate-800 mb-2">
            Analyzing Your Unique Situation
          </h2>
          <p className="text-slate-500">
            Cross-referencing tax treaties, RNOR rules & optimal asset timing for your profile
          </p>
        </div>

        {/* Progress bar */}
        <div className="mb-6">
          <div className="flex justify-between text-xs text-slate-500 mb-2">
            <span>Progress</span>
            <span>{Math.round(progress)}% complete</span>
          </div>
          <div className="h-2 bg-white/60 rounded-full overflow-hidden shadow-inner">
            <motion.div
              className="h-full bg-gradient-to-r from-emerald-400 via-blue-500 to-emerald-500 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            />
          </div>
        </div>

        {/* Stages list - scrollable */}
        <div className="flex-1 overflow-y-auto mb-6 pr-2 -mr-2">
          <div className="space-y-2">
            {STAGES.map((s, index) => {
              const isComplete = index < visualIndex;
              const isCurrent = index === visualIndex;
              // isPending used implicitly in styling

              return (
                <motion.div
                  key={s.key}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`
                    flex items-center gap-3 p-3 rounded-xl transition-all duration-300
                    ${isCurrent 
                      ? 'bg-white shadow-md border border-emerald-100' 
                      : isComplete 
                        ? 'bg-white/50' 
                        : 'bg-white/30'
                    }
                  `}
                >
                  {/* Icon */}
                  <div className={`
                    w-10 h-10 rounded-lg flex items-center justify-center text-lg
                    ${isComplete 
                      ? 'bg-emerald-100' 
                      : isCurrent 
                        ? 'bg-emerald-500 shadow-lg shadow-emerald-500/30' 
                        : 'bg-slate-100'
                    }
                  `}>
                    {isComplete ? (
                      <CheckCircle className="w-5 h-5 text-emerald-600" />
                    ) : isCurrent ? (
                      <motion.span
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ duration: 1, repeat: Infinity }}
                      >
                        {s.icon}
                      </motion.span>
                    ) : (
                      <span className="opacity-40">{s.icon}</span>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className={`
                      font-medium text-sm
                      ${isComplete 
                        ? 'text-emerald-700' 
                        : isCurrent 
                          ? 'text-slate-800' 
                          : 'text-slate-400'
                      }
                    `}>
                      {s.label}
                    </div>
                    {(isCurrent || isComplete) && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="text-xs text-slate-500 mt-0.5"
                      >
                        {s.description}
                      </motion.div>
                    )}
                  </div>

                  {/* Status indicator */}
                  {isCurrent && (
                    <Loader2 className="w-4 h-4 text-emerald-500 animate-spin" />
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Did you know? fact card */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentFactIndex}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-100 rounded-xl p-4 mb-4"
          >
            <div className="flex items-start gap-3">
              <span className="text-2xl">{currentFact?.icon}</span>
              <div>
                <div className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-1">
                  💡 Did you know? • {currentFact?.title}
                </div>
                <p className="text-sm text-amber-900">
                  {currentFact?.fact}
                </p>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Value preview cards */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          {VALUE_PREVIEWS.map((preview, index) => (
            <motion.div
              key={preview.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 + index * 0.1 }}
              className="bg-white/70 backdrop-blur-sm rounded-lg p-3 border border-slate-100 text-center"
            >
              <preview.icon className="w-5 h-5 text-emerald-500 mx-auto mb-1" />
              <div className="text-xs font-medium text-slate-700">{preview.title}</div>
              <div className="text-xs text-slate-400">{preview.subtitle}</div>
            </motion.div>
          ))}
        </div>

        {/* Footer */}
        <div className="text-center">
          <p className="text-xs text-slate-400">
            {elapsedTime < 10
              ? 'Analyzing your unique situation...'
              : elapsedTime < 18
                ? `${elapsedTime}s • Building your personalized strategy`
                : elapsedTime < 25
                  ? `${elapsedTime}s • Almost ready...`
                  : `${elapsedTime}s • Finalizing your roadmap`
            }
          </p>
        </div>
      </div>
    </div>
  );
}

export default JourneyLoading;
